import { ClampMargins, Min } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, PlotFunction } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { GrowContentKind } from "@pdfking/templates"

import { MIN_BOOK_SECURITY, PALETTE } from "#constants"
import type { DrawPage } from "#internal"

import { DrawBookPage } from "../book/DrawBookPage"
import { DrawBackCover } from "../cover/DrawBackCover"
import { DrawBackCoverBack } from "../cover/DrawBackCoverBack"
import { DrawBackFlap } from "../cover/DrawBackFlap"
import { DrawFrontCover } from "../cover/DrawFrontCover"
import { DrawFrontCoverBack } from "../cover/DrawFrontCoverBack"
import { DrawFrontFlap } from "../cover/DrawFrontFlap"
import { DrawSpine } from "../cover/DrawSpine"
import type { Composition } from "./Composition"
import { ComposePieces, Ground } from "./Composition"

/** A lorem book made of its pieces, each page by its number: on a cover's
 * grow 1 the front cover, 2 the inside of it with the ex libris, the last the
 * back cover, the one before the inside of it; any other a page of the book;
 * a page with no number is a blank. The spine
 * carries title and author on the outside; a flap is the cover's, the back's
 * before the first page, the front's after — a strip too narrow for one, a
 * board's turn-in or hinge, the ground alone. The inside is the pattern,
 * the pieces mirrored along the opening. Its pages drawn by `draw_page`, a
 * cover's too unless it has the book's (`book_covers`). Its text and cover
 * keep at least `MIN_BOOK_SECURITY` off every edge. */
export const BookContentOf =
  (draw_page: DrawPage, book_covers = true) =>
  (c: Composition): PageShape[] => {
    const is_cover = c.is_cover && book_covers
    const { is_front, measure, opening_top, pieces, seed, short, total_pages } = c
    const first_page = pieces.findIndex(piece => piece.enum === GrowContentKind.PAGE)
    return [
      ...Ground(c),
      ...ComposePieces(c, {
        [GrowContentKind.FLAP]: ({ box, index }) => {
          if (!is_front || Min(box.width, box.height) < short / 4) return []
          return (index < first_page ? DrawBackFlap : DrawFrontFlap)({ box, measure, seed, short })
        },
        [GrowContentKind.PAGE]: ({ box, number, reach, security: given }) => {
          const security = ClampMargins(given, MIN_BOOK_SECURITY)
          if (NullQ(number))
            return [{ enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.paper, position: box }]
          if (is_cover && number === 1)
            return DrawFrontCover({ bleed: reach, box, measure, security, seed })
          if (is_cover && number === 2) return DrawFrontCoverBack({ bleed: reach, box, seed })
          if (is_cover && number === total_pages)
            return DrawBackCover({ bleed: reach, box, security, seed })
          if (is_cover && number === total_pages - 1)
            return DrawBackCoverBack({ bleed: reach, box, seed })
          return [
            {
              enum: PlotFunction.PLOT_GROUP,
              matrix: GroupMatrix({ translate: { x: box.x, y: box.y } }),
              shapes: draw_page({ ...c, bleed: reach, page_number: number, security, size: box }),
            },
          ]
        },
        [GrowContentKind.SPINE]: ({ box }) =>
          is_front ? DrawSpine({ box, opening_top, seed, short }) : [],
      }),
    ]
  }

/** The lorem book, every page a page of prose. */
export const BookContent = BookContentOf(DrawBookPage)
